<template>
  <v-card :loading="isLoading">
    <v-card-text>
      <v-data-table
        v-bind="dataTableAttrs"
        :headers="headersShown"
        :items="list"
        :loading="isLoading"
        :options.sync="options"
        :server-items-length="total"
        :items-per-page.sync="itemsPerPage"
        v-model="selectedItems"
        @update:items-per-page="handleItemsPerPageUpdated"
        :item-class="getRowClass"
      >
        <template v-slot:top>
          <rs-table-title-bar
            v-if="$refs.headSearch"
            title="POS Kullanım İstatistikleri"
            icon="mdi-credit-card"
            @clear-filters="handleClearFilters"
            @reload="loadList"
            :search.sync="search.query"
            v-bind="titleBarAttrs"
            :hide-edit="true"
          />

          <v-row dense class="mt-2 mb-2">
            <!-- Ödeme Durumu -->
            <v-col cols="12" md="3">
              <div class="text-caption text--secondary mb-1">
                Ödeme Durumu
              </div>
              <v-btn-toggle
                v-model="search.has_payments"
                dense
                color="primary"
              >
                <v-btn :value="null" small> Tümü </v-btn>
                <v-btn :value="true" small> Ödemesi Var </v-btn>
                <v-btn :value="false" small> Ödeme Yok </v-btn>
              </v-btn-toggle>
            </v-col>

            <!-- Hızlı Tarih Seçimi -->
            <v-col cols="12" md="5">
              <div class="text-caption text--secondary mb-1">
                Hızlı Tarih Seçimi
              </div>
              <div class="d-flex flex-wrap align-start">
                <v-btn
                  small
                  text
                  class="mr-1 mb-1"
                  @click="setDatePreset('last7')"
                >
                  Son 7 Gün
                </v-btn>

                <v-btn
                  small
                  text
                  class="mr-1 mb-1"
                  @click="setDatePreset('thisMonth')"
                >
                  Bu Ay
                </v-btn>

                <v-btn
                  small
                  text
                  class="mr-1 mb-1"
                  @click="setDatePreset('lastMonth')"
                >
                  Geçen Ay
                </v-btn>

                <v-btn
                  small
                  text
                  class="mr-1 mb-1"
                  @click="setDatePreset('ytd')"
                >
                  Yılbaşından Bugüne
                </v-btn>

                <v-btn
                  small
                  text
                  class="mr-1 mb-1"
                  @click="setDatePreset('clear')"
                >
                  Temizle
                </v-btn>
              </div>
            </v-col>

            <!-- Başlangıç Tarihi -->
            <v-col cols="6" sm="6" md="2">
              <v-text-field
                v-model="search.created_at.min"
                label="Başlangıç Tarihi"
                type="date"
                dense
                hide-details="auto"
                clearable
              />
            </v-col>

            <!-- Bitiş Tarihi -->
            <v-col cols="6" sm="6" md="2">
              <v-text-field
                v-model="search.created_at.max"
                label="Bitiş Tarihi"
                type="date"
                dense
                hide-details="auto"
                clearable
              />
            </v-col>
          </v-row>

          <!-- KPI Row 1: Counts and Amounts -->
          <v-row v-if="!isLoading && list.length" class="mt-4 mb-2" dense>
            <v-col cols="6" sm="6" md="3">
              <v-sheet outlined class="pa-2 text-center">
                <div class="caption mb-1">Toplam TYA</div>
                <div class="subtitle-1 font-weight-bold">
                  {{ kpiTotals?.clusterCount }}
                </div>
              </v-sheet>
            </v-col>

            <v-col cols="6" sm="6" md="3">
              <v-sheet outlined class="pa-2 text-center">
                <div class="caption mb-1">Toplam Hacim (TYA)</div>
                <div class="subtitle-1 font-weight-bold">
                  <rs-table-cell-number
                    price
                    :value="kpiTotals?.amountForCluster"
                  />
                </div>
              </v-sheet>
            </v-col>

            <v-col cols="6" sm="6" md="3">
              <v-sheet outlined class="pa-2 text-center">
                <div class="caption mb-1">Toplam Hizmet Bedeli</div>
                <div class="subtitle-1 font-weight-bold">
                  <rs-table-cell-number
                    price
                    :value="kpiTotals?.totalCommission"
                  />
                </div>
              </v-sheet>
            </v-col>

            <v-col cols="6" sm="6" md="3">
              <v-sheet outlined class="pa-2 text-center">
                <div class="caption mb-1">RoboSay Komisyon</div>
                <div class="subtitle-1 font-weight-bold">
                  <rs-table-cell-number
                    price
                    :value="kpiTotals?.totalRobosayCommission"
                  />
                </div>
              </v-sheet>
            </v-col>
          </v-row>

          <!-- KPI Row 2: Percentages -->
          <v-row v-if="!isLoading && list.length" class="mb-4" dense>
            <v-col cols="6" sm="4" md="4">
              <v-sheet outlined class="pa-2 text-center">
                <div class="caption mb-1">Ortalama Komisyon Oranı</div>
                <div class="subtitle-1 font-weight-bold">
                  {{
                    kpiTotals?.commissionRate !== null
                      ? kpiTotals?.commissionRate.toFixed(2) + " %"
                      : "-"
                  }}
                </div>
              </v-sheet>
            </v-col>

            <v-col cols="6" sm="4" md="4">
              <v-sheet outlined class="pa-2 text-center">
                <div class="caption mb-1">Mobil App Kullanım Oranı</div>
                <div class="subtitle-1 font-weight-bold">
                  <span v-if="kpiTotals.mobileAppUsageRate !== null">
                    {{ kpiTotals.mobileAppUsageRate.toFixed(2) }} %
                  </span>
                  <span v-else>-</span>
                </div>
              </v-sheet>
            </v-col>

            <v-col cols="6" sm="4" md="4">
              <v-sheet outlined class="pa-2 text-center">
                <div class="caption mb-1">
                  Ödeme Yapan Mobil Kullanıcı Oranı
                </div>
                <div class="subtitle-1 font-weight-bold">
                  <span v-if="kpiTotals.payingMobileUserRate !== null">
                    {{ kpiTotals.payingMobileUserRate.toFixed(2) }} %
                  </span>
                  <span v-else>-</span>
                </div>
              </v-sheet>
            </v-col>
          </v-row>
        </template>
        <template v-slot:body.prepend="{ headers }">
          <rs-table-head-search
            :headers="headers"
            :search.sync="search"
            ref="headSearch"
          />
        </template>
        <template v-slot:body.append="{ headers }">
          <rs-table-foot-totals :headers="headers" :totals="footTotals">
            <!-- Mobil App Kullanım Oranı (%) footer -->
            <template #foot.total_mobile_user_rate>
              <span
                v-if="
                  footTotals &&
                  footTotals.mobile_app_usage_rate !== null &&
                  footTotals.mobile_app_usage_rate !== undefined
                "
              >
                {{
                  (Number(footTotals.mobile_app_usage_rate) * 100).toFixed(2)
                }}
                %
              </span>
              <span v-else>-</span>
            </template>

            <!-- Ödeme Yapan Mobil Kullanıcı Sayısı footer -->
            <template #foot.mobile_user_count>
              <span
                v-if="
                  footTotals &&
                  footTotals.total_paying_mobile_user_count !== null &&
                  footTotals.total_paying_mobile_user_count !== undefined
                "
              >
                {{ footTotals.total_paying_mobile_user_count }}
              </span>
              <span v-else>-</span>
            </template>

            <!-- Ödeme Yapan Mobil Kullanıcı Oranı (%) footer -->
            <template #foot.paying_mobile_user_rate>
              <span
                v-if="
                  footTotals &&
                  footTotals.paying_mobile_user_rate !== null &&
                  footTotals.paying_mobile_user_rate !== undefined
                "
              >
                {{
                  (Number(footTotals.paying_mobile_user_rate) * 100).toFixed(2)
                }}
                %
              </span>
              <span v-else>-</span>
            </template>
          </rs-table-foot-totals>
        </template>

        <!-- eslint-disable-next-line -->
        <template v-slot:item.cluster="{ item }">
          <a
            @click.prevent="goToCCPayments(item.cluster_id)"
            class="text-link"
            style="cursor: pointer; text-decoration: underline"
          >
            {{ item.cluster }}
          </a>
        </template>

        <!-- KK Entegrasyonu -->
        <template v-slot:item.has_active_integration="{ value }">
          <v-chip x-small :color="value ? 'success' : 'grey'" dark>
            {{ value ? 'Var' : 'Yok' }}
          </v-chip>
        </template>

        <!-- Kredi Kartı Hacim -->
        <template v-slot:item.amount_for_cluster="{ value }">
          <rs-table-cell-number price :value="value" />
        </template>

        <!-- Toplam Komisyon -->
        <template v-slot:item.total_commission="{ value }">
          <rs-table-cell-number price :value="value" />
        </template>

        <!-- RoboSay Komisyon -->
        <template v-slot:item.robosay_commission="{ value }">
          <rs-table-cell-number price :value="value" />
        </template>

        <!-- PARAM Komisyon -->
        <template v-slot:item.param_commission="{ value }">
          <rs-table-cell-number price :value="value" />
        </template>

        <!-- Kredi Kartı Toplam Hacim -->
        <template v-slot:item.total_amount="{ value }">
          <rs-table-cell-number price :value="value" />
        </template>

        <!-- Mobil App Kullanım Oranı (%) -->
        <template v-slot:item.total_mobile_user_rate="{ value }">
          <span v-if="value !== null && value !== undefined">
            {{ (Number(value) * 100).toFixed(2) }} %
          </span>
          <span v-else>-</span>
        </template>

        <!-- Ödeme Yapan Mobil Kullanıcı Oranı (%) -->
        <template v-slot:item.paying_mobile_user_rate="{ value }">
          <span v-if="value !== null && value !== undefined">
            {{ (Number(value) * 100).toFixed(2) }} %
          </span>
          <span v-else>-</span>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>


<script>
import { debounce } from "@/core/plugins/debounce";
import { filtersToURL, hasExport, hasPermissions } from "@/view/mixins";
import { isPage } from "../../../mixins";

export default {
  name: "POSStatistics",
  mixins: [filtersToURL, hasExport, hasPermissions, isPage],
  watch: {
    options: {
      handler() {
        this.setURLParams();
      },
      deep: true,
    },
    search: {
      handler: debounce(function () {
        this.options.page = 1;
        this.setURLParams();
      }, 250),
      deep: true,
    },
  },
  mounted() {
    this.generateExportColumns(this.headers);

    if (!this.search.created_at) {
      this.$set(this.search, "created_at", {
        min: null,
        max: null,
      });
    }
  },
  data() {
    return {
      pageMeta: {
        title: "POS Kullanım İstatistikleri",
      },
      search: {
        query: null,
        trigger: null,
        created_at: {
          min: null,
          max: null,
        },
        has_payments: null,
      },
      options: {
        sortBy: ["cluster"],
        sortDesc: [false],
      },
      dataTableAttrs: {
        footerProps: {
          disableItemsPerPage: false,
        },
      },
      titleBarAttrs: {
        exportUrl: "cc-payments/summary-by-cluster",
        exportParams: this.getParams,
      },
      headers: [
        {
          text: "Yönetim Şirketi",
          value: "company_name",
          searchable: "text",
          hide: () => true,
        },
        {
          text: "TYA Adı",
          value: "cluster",
          searchable: "text",
          searchValue: "query",
        },
        {
          text: "KK Entegrasyonu",
          value: "has_active_integration",
          searchable: false,
          align: "center",
          notPrice: true,
        },
        {
          text: "BB Sayısı",
          value: "total_house_count",
          searchable: false,
          align: "end",
          notPrice: true,
          sortable: true,
        },
        {
          text: "Sakin Sayısı",
          value: "total_resident_count",
          searchable: false,
          align: "end",
          notPrice: true,
        },
        {
          text: "Mobil Kullanıcı Sayısı",
          value: "total_mobile_user_count",
          searchable: false,
          align: "end",
          notPrice: true,
        },
        {
          text: "Mobil App Kullanım Oranı (%)",
          value: "total_mobile_user_rate",
          searchable: false,
          align: "end",
          notPrice: true,
        },
        {
          text: "Ödeme Yapan Mobil Kullanıcı Sayısı",
          value: "mobile_user_count",
          searchable: false,
          align: "end",
          notPrice: true,
        },
        {
          text: "Ödeme Yapan Mobil Kullanıcı Oranı (%)",
          value: "paying_mobile_user_rate",
          searchable: false,
          align: "end",
          notPrice: true,
        },
        {
          text: "Kredi Kartı Hacim",
          value: "amount_for_cluster",
          searchable: false,
          align: "end",
        },
        {
          text: "Toplam Komisyon",
          value: "total_commission",
          searchable: false,
          align: "end",
        },
        {
          text: "RoboSay Komisyon",
          value: "robosay_commission",
          searchable: false,
          align: "end",
        },
        {
          text: "PARAM Komisyon",
          value: "param_commission",
          searchable: false,
          align: "end",
        },
        {
          text: "Kredi Kartı Toplam Hacim",
          value: "total_amount",
          searchable: false,
          align: "end",
        },
      ],
    };
  },
  computed: {
    kpiTotals() {
      const t = this.footTotals || {};
      const toNumberOrNull = (v) =>
        v === null || v === undefined || v === "" ? null : Number(v);

      return {
        // adetler
        clusterCount: t.cluster_count ?? null,
        totalHouseCount: toNumberOrNull(t.total_house_count),
        totalResidentCount: toNumberOrNull(t.total_resident_count),
        totalMobileUserCount: toNumberOrNull(t.total_mobile_user_count),
        totalPayingMobileUserCount: toNumberOrNull(
          t.total_paying_mobile_user_count
        ),

        // tutarlar
        amountForCluster: toNumberOrNull(t.amount_for_cluster),
        totalAmount: toNumberOrNull(t.total_amount),
        totalCommission: toNumberOrNull(t.total_commission),
        totalRobosayCommission: toNumberOrNull(t.total_robosay_commission),
        totalParamCommission: toNumberOrNull(t.total_param_commission),

        mobileAppUsageRate: (() => {
          const v = toNumberOrNull(t.mobile_app_usage_rate);
          return v === null ? null : v * 100;
        })(),
        payingMobileUserRate: (() => {
          const v = toNumberOrNull(t.paying_mobile_user_rate);
          return v === null ? null : v * 100;
        })(),
        commissionRate: (() => {
          const v = toNumberOrNull(t.overall_avg_commission_rate);
          return v === null ? null : v * 100;
        })(),
      };
    },
  },
  methods: {
    getParams() {
      const params = Object.assign(
        this.buildSearchParams(this.search, this.options)
      );

      if (!this.search.created_at?.min && !this.search.created_at?.max) {
        delete params["created_at"];
        delete params["created_at[min]"];
        delete params["created_at[max]"];
      }

      // Remove has_payments if null (show all)
      if (this.search.has_payments === null) {
        delete params["has_payments"];
      }

      return params;
    },
    handleClearFilters() {
      this.$refs.headSearch && this.$refs.headSearch.reset();
      this.$set(this.search, "created_at", { min: null, max: null });
      this.$set(this.search, "has_payments", null);
    },
    goToCCPayments(clusterId) {
      this.$router.push({
        name: "activities.cc-payments.list",
        query: {
          "cluster_id[]": [clusterId],
          page: 1,
        },
      });
    },
    getRowClass(item) {
      const mobileRate =
        item.total_resident_count > 0
          ? item.total_mobile_user_count / item.total_resident_count
          : 0;

      const amount = item.amount_for_cluster
        ? parseFloat(item.amount_for_cluster)
        : 0;

      const classes = [];

      if (item.total_resident_count >= 0 && mobileRate < 0.2) {
        classes.push("pos-row--low-mobile");
      }

      if (amount >= 1000) {
        classes.push("pos-row--high-volume");
      }

      return classes.join(" ");
    },
    setDatePreset(preset) {
      if (
        !this.search.created_at ||
        typeof this.search.created_at !== "object"
      ) {
        this.$set(this.search, "created_at", {
          min: null,
          max: null,
        });
      }

      const today = new Date();
      const format = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");

        return `${year}-${month}-${day}`;
      };

      const startOfMonth = (date) =>
        new Date(date.getFullYear(), date.getMonth(), 1);

      const endOfMonth = (date) =>
        new Date(date.getFullYear(), date.getMonth() + 1, 0);

      const startOfYear = (date) => new Date(date.getFullYear(), 0, 1);

      const addDays = (date, days) =>
        new Date(date.getTime() + days * 24 * 60 * 60 * 1000);

      if (preset === "clear") {
        this.search.created_at.min = null;
        this.search.created_at.max = null;
        return;
      }

      if (preset === "last7") {
        this.search.created_at.min = format(addDays(today, -7));
        this.search.created_at.max = format(today);
        return;
      }

      if (preset === "thisMonth") {
        this.search.created_at.min = format(startOfMonth(today));
        this.search.created_at.max = format(endOfMonth(today));
        return;
      }

      if (preset === "lastMonth") {
        const lastMonth = new Date(
          today.getFullYear(),
          today.getMonth() - 1,
          1
        );
        this.search.created_at.min = format(startOfMonth(lastMonth));
        this.search.created_at.max = format(endOfMonth(lastMonth));
        return;
      }

      if (preset === "ytd") {
        this.search.created_at.min = format(startOfYear(today));
        this.search.created_at.max = format(today);
        return;
      }
    },
    loadList() {
      if (this.isLoading) {
        return;
      }

      this.isLoading = true;
      this.list = [];
      this.selectedItems = [];
      this.footTotals = [];

      const params = this.getParams();

      this.$api
        .query(`/cc-payments/summary-by-cluster`, {
          params,
        })
        .then((response) => {
          if (response.data && response.data.data) {
            this.list = response.data.data;
          }

          if (response.data && response.data.meta) {
            this.total = response.data.meta.total;
            this.options.page = response.data.meta.current_page;
            this.itemsPerPage = response.data.meta.per_page;
            this.options.itemsPerPage = this.itemsPerPage;
            this.footTotals = response.data.meta.totals || {};
          }
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style>
.pos-row--low-mobile {
  background-color: rgba(247, 181, 7, 0.25) !important;
}

.pos-row--high-volume {
  background-color: rgba(76, 175, 80, 0.25) !important;
}
</style>
